import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useState } from "react";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Badge,
} from "@transfr-inc/dashboard-components";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";
import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

import { Status } from "@src/constants/integration-provider.model";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

import IntegrationDate from "./integration-date";

import "./sync-history-item.scss";

const responsiveQueryForTable = {
  "small-panel": {
    maxWidth: 500,
  },
  "large-panel": {
    minWidth: 500,
  },
};

const SyncHistoryItem = ({ syncRecord }) => {
  const { status, executionDateTime, message, results, jobExecutionId } =
    syncRecord;
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const { integrationService } = services;

  const { loading: downloadingCSV, sendRequest: exportSyncData } =
    useApiRequest(
      () => integrationService.getSyncDetails(jobExecutionId),
      false,
    );

  const isFailed = status === Status.FAILED;
  const isPending = status === Status.PENDING;
  const isSuccessful = !isFailed && !isPending;

  const dayOfWeek = new Date(executionDateTime).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });

  const [responsiveStylesForTable, accordionRef] = useContainerQuery(
    responsiveQueryForTable,
  );

  const typeLabels = {
    student: `${getLearnerTerm({ plural: true })}`,
    teacher: "Instructors",
    classrooms: "Classrooms",
  };

  const eventLabels = {
    create: "created",
    update: "updated",
    delete: "deleted",
    error: "errors",
  };

  const labeledSyncRecord = results.map((record, i) => ({
    ...record,
    typeLabel: typeLabels[record.type],
  }));

  const eventDetailsCellFn = (eventType) => (value, rowData) => {
    return value > 0 ? (
      <button
        className="link-button"
        onClick={() =>
          setShowDetailsModal({
            eventType,
            resourceType: rowData.type,
            typeLabel: typeLabels[rowData.type],
            eventLabel: eventLabels[eventType],
          })
        }
      >
        {value}
      </button>
    ) : (
      <BasicCell value={value} />
    );
  };

  const columns = [
    new Column(
      "typeLabel",
      "data type",
      ColumnType.BASIC,
      new ColumnOptions({
        sortingEnabled: true,
      }),
    ),
    new Column(
      "create",
      eventLabels.create,
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: eventDetailsCellFn("create"),
      }),
    ),
    new Column(
      "update",
      eventLabels.update,
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: eventDetailsCellFn("update"),
      }),
    ),
    new Column(
      "error",
      eventLabels.error,
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value) =>
          value > 0 ? (
            <div>
              <Badge
                className="error error-count"
                value={
                  <>
                    <FontAwesomeIcon
                      icon={["fa-solid", "triangle-exclamation"]}
                    />
                    {value}
                  </>
                }
              />
            </div>
          ) : (
            <BasicCell value={value} />
          ),
      }),
    ),
  ];

  return (
    <div className={`sync-history-item ${status.toLowerCase()}`}>
      <Accordion isOpen hideExpandProperties ref={accordionRef}>
        <AccordionHeader>
          <AccordionHeaderName>
            <div className="header-name-container">
              <div className="date">
                <Badge
                  value={dayOfWeek}
                  className={`badge ${isFailed ? "error" : ""}`}
                />
                <IntegrationDate date={executionDateTime} />
              </div>
              <div className="message">
                {isFailed && (
                  <FontAwesomeIcon
                    icon={["fa-solid", "triangle-exclamation"]}
                  />
                )}
                {(isFailed || isPending) && message}
              </div>
            </div>
          </AccordionHeaderName>
        </AccordionHeader>
        {isSuccessful && (
          <AccordionBody>
            <div className="data-table-container">
              <DataTable
                className={clsx(
                  "sync-history-item__results-table",
                  responsiveStylesForTable,
                )}
                columns={columns}
                data={labeledSyncRecord ?? []}
                keyFields={["type"]}
                defaultSortPropName={"type"}
                defaultSortDirection={SortDirection.DESCENDING}
                responsiveStyles={responsiveStylesForTable}
              />
            </div>
          </AccordionBody>
        )}
      </Accordion>
      <div className="csv-download-container">
        <span>Download .csv to get sync details.</span>
        <Button
          size="small"
          icon={["fa-light", "file-export"]}
          onClick={exportSyncData}
          disabled={downloadingCSV}
        >
          Export Data (.csv)
        </Button>
      </div>
      {showDetailsModal && // TODO: Add SyncEventDetailsModal
        null}
    </div>
  );
};

export default SyncHistoryItem;
